import { CommonModule, DatePipe } from '@angular/common';
import { Component, Input, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { SvgIcon, SchoolData, SchoolAddress as Address } from '@k12/models';
import { PhonePipe } from '@k12/pipes';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-school-info',
  standalone: true,
  imports: [CommonModule, DatePipe, MatButtonModule],
  templateUrl: './school-info.component.html',
  providers: [PhonePipe]
})
export class SchoolInfoComponent {
  private readonly phonePipe = inject(PhonePipe);
  private _schoolData: SchoolData = {};

  @Input() set schoolData(value: SchoolData) {
    this._schoolData = Array.isArray(value) ? {} : value || {};
  }
  get schoolData(): SchoolData {
    return this._schoolData;
  }

  readonly editIcon: SvgIcon = SvgIcon.Edit;

  // Returns a plain string (never SafeHtml), so [innerHTML] bindings using this are
  // auto-sanitized by Angular; only a safe <br> is ever added between address lines.
  getAddressWithLineBreaks(address?: Address | string): string {
    if (!address) {
      return '-';
    }

    // County is shown in its own field, so strip it from a preformatted string.
    if (typeof address === 'string') {
      const lines = address.replaceAll('<br>', '\n').split('\n').map((line) => line.trim()).filter(Boolean);
      if (lines.length === 0) {
        return '-';
      }

      const lastLine = lines.at(-1) ?? '';
      const parts = lastLine.split(',').map((part) => part.trim()).filter(Boolean);

      if (parts.length >= 4) {
        const [city, , state, zip] = parts;
        lines[lines.length - 1] = this.formatCityStateZip({ city, state, zip });
      } else if (parts.length === 3) {
        const [city, state, zip] = parts;
        lines[lines.length - 1] = this.formatCityStateZip({ city, state, zip });
      }

      return lines.join('<br>');
    }

    const addressParts: string[] = [];
    if (address.address1) addressParts.push(address.address1);
    if (address.address2) addressParts.push(address.address2);

    const cityStateZip = this.formatCityStateZip(address);
    if (cityStateZip) {
      addressParts.push(cityStateZip);
    }

    return addressParts.join('<br>') || '-';
  }

  private formatCityStateZip(address: Pick<Address, 'city' | 'state' | 'zip'>): string {
    const city = address.city ? String(address.city).trim() : '';
    let stateName = '';
    if (address.state) {
      stateName = typeof address.state === 'object' ? address.state.name : address.state;
      stateName = String(stateName ?? '').trim();
    }
    const zip = address.zip ? String(address.zip).trim() : '';
    const stateZip = [stateName, zip].filter(Boolean).join(' ');

    return [city, stateZip].filter(Boolean).join(', ');
  }

  get county(): string {
    return this.schoolData.addresses?.[0]?.county || '-';
  }

  get phoneExtension(): string {
    return this.schoolData.phoneExtension?.trim() || '';
  }

  get formattedPhone(): string {
    const raw = this.schoolData.phone?.trim();
    if (!raw) {
      return '-';
    }

    const phone = this.phonePipe.transform(raw);
    const extension = this.phoneExtension;

    return extension ? `${phone} ext. ${extension}` : phone;
  }
}
